import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartData,
  ChartDataset,
  ScriptableContext,
  LegendOptions,
} from "chart.js";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import { Chart } from "react-chartjs-2";

ChartJS.register(MatrixController, MatrixElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

interface ProcessedEvictionDataAggregate {
  evictedindividuals: number;
  evictedhouseholds: number;
  evictionsByRegionPerYear: Record<string, { evictions: number }>;
  evictionsByRegionPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>;
  evictionsByYearPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>;
  preventionsByYearPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>;
  preventionsByRegionPerMonth: Record<string, number>;
  evictionsByReasonsPerYear: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>;
  evictionsByRegionPerReason: Record<string, Record<string, number>>;
  evictionsByRegionPerYearByReason: Record<string, Record<string, Record<string, { total_evictions_individuals: number }>>>;
  overallEvictionsByYear: Record<string, number>;
  overallEvictionsByMonth: Record<string, number>;
  preventionsByRegionPerYear: Record<string, number>;
  overallPreventionsByYear: Record<string, number>;
  overallPreventionsByMonth: Record<string, number>;
  overallEvictedRegions: Record<string, number>;
  overallEvictionReasons: Record<string, number>;
  overallEvictedDistricts: Record<string, number>;
}

type MatrixDataPoint = {
  x: string | number;
  y: string | number;
};

interface HeatmapDataPoint extends MatrixDataPoint {
  v: number;
}

const HeatmapChart = ({ data }: { data: ProcessedEvictionDataAggregate }) => {
  const [chartData, setChartData] = useState<ChartData<"matrix", HeatmapDataPoint[], string>>({
    datasets: []
  });

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const regions = Object.keys(data.evictionsByRegionPerMonth || {});

  // Configurable values
  const steps = 6;
  const colorScale = ['#F4F5F9', '#C6CCDB', '#8994B0', '#566287', '#354470', '#1B2653'];


  // Preprocess min/max for value range
  const matrixData: HeatmapDataPoint[] = [];
  const allValues: number[] = [];

  regions.forEach(region => {
    const monthlyData = data.evictionsByRegionPerMonth[region] || {};
    months.forEach(month => {
      const value = monthlyData[month]?.total_evictions_individuals ?? 0;
      matrixData.push({ x: month, y: region, v: value });
      allValues.push(value);
    });
  });

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const stepSize = (max - min) / steps;

  const getColor = (value: number) => {
    const index = Math.floor((value - min) / stepSize);
    return colorScale[Math.min(index, colorScale.length - 1)];
  };
  
  useEffect(() => {
    if (!data || !regions.length) return;

    const dataset: ChartDataset<"matrix", HeatmapDataPoint[]> = {
      data: matrixData,
      backgroundColor: (ctx: ScriptableContext<"matrix">) => {
        const point = ctx.dataset.data[ctx.dataIndex] as HeatmapDataPoint;
        return getColor(point?.v ?? 0);
      },
      borderWidth: 1,
      borderColor: '#fff',
      width: ({ chart }) => {
        if (!chart.chartArea) return 20;
        return (chart.chartArea.width / months.length) - 1;
      },
      height: ({ chart }) => {
        if (!chart.chartArea) return 20;
        return (chart.chartArea.height / regions.length) - 1;
      }
    };

    setChartData({ datasets: [dataset] });
  }, [data]);

  const generateLegendLabels = () => {
    const labels = [];
    for (let i = 0; i < steps; i++) {
      const from = Math.round((min + i * stepSize) / 100) * 100;
      const to = Math.round((min + (i + 1) * stepSize) / 100) * 100;
      labels.push({
        text: `Between ${from.toLocaleString()} and ${to.toLocaleString()}`,
        fillStyle: colorScale[i]
      });
    }
    return labels.reverse(); // Reverse the order to match the legend
  };

  return (
    <div className="p-4 bg-white shadow-md rounded-md flex items-center justify-center" style={{ height: "500px", width: "100%" }}>
      {regions.length > 0 ? (
        <Chart
          type="matrix"
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: false,
                text: "Monthly Evictions by Region",
                font: {
                  size: 16,
                  family: "'Inter', 'Segoe UI', sans-serif"
                },
                padding: {
                  bottom: 15
                },
                color: '#2d3748'
              },
              tooltip: {
                callbacks: {
                  label: (tooltipItem: TooltipItem<"matrix">) => {
                    const raw = tooltipItem.raw as unknown as HeatmapDataPoint;
                    return `${raw.y}: ${raw.v.toLocaleString()} evictions in ${raw.x}`;
                  }
                },
                backgroundColor: "#fff",
                titleColor: "#000",
                bodyColor: "#333",
                borderColor: "#ddd",
                borderWidth: 1,
                padding: 10,
              },
              legend: {
                display: true,
                position: 'top',
                align: 'center',
                labels: {
                  boxWidth: 20,
                  boxHeight: 12,
                  padding: 10,
                  generateLabels: generateLegendLabels
                },
              }
            },
            scales: {
              x: {
                type: "category",
                labels: months,
                position: "bottom",
                grid: { display: false },
                ticks: {
                  maxRotation: 45,
                  autoSkip: false
                }
              },
              y: {
                type: "category",
                labels: regions,
                grid: { display: false },
                ticks: {
                  mirror: false,
                  display: false
                }
              }
            }
          }}
        />
      ) : (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      )}
    </div>
  );
};

export default HeatmapChart;